/**
 * Attack Action Constants
 *
 * Defines balance constants for attack actions (negative_ads and opposition_research).
 * Used by the attack resolver during tick processing.
 *
 * @module constants/attacks
 */

// ============================================================================
// Types
// ============================================================================

export type AttackSubtype = 'negative_ads' | 'opposition_research'
export type ScandalType = 'corruption' | 'hypocrisy' | 'personal_misconduct' | 'policy_failure'
export type ScandalSeverity = 'minor' | 'moderate' | 'major'

/**
 * Configuration for an attack subtype.
 */
export interface AttackSubtypeConfig {
  key: AttackSubtype
  label: string
  description: string
  icon: string
  apRange: [number, number]
  riskDescription: string
}

/**
 * Effects applied by a scandal of a given severity.
 */
export interface ScandalSeverityEffects {
  recognition: number // Change to party recognition (negative)
  loyalty: number // Change to cohort loyalty (negative)
  momentum: number // Change to party momentum (negative)
  prestige: number // Change to party prestige (negative)
  duration: number // How many ticks the scandal remains active
}

// ============================================================================
// Attack Subtype Configurations
// ============================================================================

export const ATTACK_SUBTYPES: Record<AttackSubtype, AttackSubtypeConfig> = {
  negative_ads: {
    key: 'negative_ads',
    label: 'Negative Ads',
    description: 'Direct attack advertising against a rival party',
    icon: '📺',
    apRange: [2, 5],
    riskDescription: 'Backfire risk if voters perceive ads as unfair',
  },
  opposition_research: {
    key: 'opposition_research',
    label: 'Opposition Research',
    description: 'Investigate rival party for scandals',
    icon: '🔍',
    apRange: [2, 5],
    riskDescription: 'Research may find nothing',
  },
} as const

// ============================================================================
// Negative Ads Constants
// ============================================================================

export const NEGATIVE_ADS = {
  /**
   * Base support reduction per AP spent.
   * 2 AP = 3% reduction, 5 AP = 7.5% reduction
   * @tuning Increase for more damaging ads, decrease for weaker ads
   */
  BASE_EFFECT_PER_AP: 1.5,

  /**
   * Efficiency multiplier for national scope ads.
   * National ads affect all districts but at reduced per-district effectiveness.
   * @tuning Increase to make national attacks more effective
   */
  NATIONAL_EFFICIENCY: 0.7,

  /**
   * Efficiency multiplier for district-targeted ads.
   * Focused attacks are more effective in the targeted area.
   * @tuning Increase to make focused attacks more powerful
   */
  DISTRICT_EFFICIENCY: 1.2,

  /**
   * Base probability of attack backfiring (15%).
   * @tuning Increase to make attacks riskier
   */
  BASE_BACKFIRE_CHANCE: 0.15,

  /**
   * Backfire bonus when attacker has low prestige (<30).
   * Low reputation makes negative ads less credible.
   * @tuning Increase to punish low-prestige attackers more
   */
  LOW_PRESTIGE_BACKFIRE_BONUS: 0.10,

  /**
   * Prestige threshold below which backfire bonus applies.
   */
  LOW_PRESTIGE_THRESHOLD: 30,

  /**
   * Additional backfire chance per AP above 3.
   * Aggressive attacks are more likely to be seen as unfair.
   * @tuning Increase to discourage heavy attack investment
   */
  HIGH_INTENSITY_BACKFIRE_BONUS: 0.05,

  /**
   * Intensity threshold above which backfire bonus applies.
   */
  HIGH_INTENSITY_THRESHOLD: 3,

  /**
   * Backfire reduction if target has recent scandals.
   * Attacks seem more justified against parties with existing issues.
   * @tuning Increase to make scandal-plagued parties easier targets
   */
  TARGET_SCANDAL_BACKFIRE_REDUCTION: 0.05,

  /**
   * Additional backfire chance if attacker already ran negative ads this tick.
   * Voter fatigue from excessive negative campaigning.
   * @tuning Increase to discourage multiple attacks per tick
   */
  REPEAT_ATTACK_BACKFIRE_BONUS: 0.10,

  /**
   * Effect reduction multiplier when attack backfires.
   * Backfired attacks still have some effect on target, but reduced.
   * @tuning Decrease to make backfires more punishing
   */
  BACKFIRE_EFFECT_REDUCTION: 0.5,

  /**
   * Reputation/prestige loss for attacker on backfire.
   * @tuning Increase to make backfires more costly
   */
  BACKFIRE_REPUTATION_LOSS: 5,

  /**
   * Momentum loss for attacker on backfire.
   */
  BACKFIRE_MOMENTUM_LOSS: 8,

  /**
   * Small recognition boost attacker gets from running ads.
   * Even negative ads increase visibility of the attacker.
   * @tuning Increase to reward attackers with visibility
   */
  ATTACKER_RECOGNITION_BOOST: 1.0,

  /**
   * Maximum backfire probability cap.
   * Ensures attacks aren't completely futile.
   */
  MAX_BACKFIRE_CHANCE: 0.60,

  /**
   * Minimum backfire probability floor.
   * All attacks have at least some risk.
   */
  MIN_BACKFIRE_CHANCE: 0.05,
} as const

// ============================================================================
// Opposition Research Constants
// ============================================================================

export const OPPOSITION_RESEARCH = {
  /**
   * Base discovery chance before modifiers (20%).
   * @tuning Increase to make research more likely to succeed
   */
  BASE_DISCOVERY_CHANCE: 0.20,

  /**
   * Additional discovery chance per AP spent (10% per AP).
   * 2 AP = 40%, 5 AP = 70% base chance
   * @tuning Increase to reward higher investment
   */
  DISCOVERY_CHANCE_PER_AP: 0.10,

  /**
   * Maximum discovery chance after all modifiers (85%).
   * Even with max investment, discovery isn't guaranteed.
   * @tuning Increase to make research more reliable
   */
  MAX_DISCOVERY_CHANCE: 0.85,

  /**
   * Minimum discovery chance floor (10%).
   * Even light research has a small chance of success.
   */
  MIN_DISCOVERY_CHANCE: 0.10,

  /**
   * Discovery bonus per point of scandal_prone trait on target candidates.
   * Scandal-prone candidates are easier to find dirt on.
   * @tuning Increase to make scandal_prone trait more impactful
   */
  SCANDAL_PRONE_BONUS_PER_POINT: 0.003,

  /**
   * Discovery bonus if target party has low average candidate loyalty (<50).
   * Disgruntled members are more likely to leak information.
   * @tuning Increase to punish low-loyalty parties
   */
  LOW_LOYALTY_BONUS: 0.10,

  /**
   * Loyalty threshold below which low loyalty bonus applies.
   */
  LOW_LOYALTY_THRESHOLD: 50,

  /**
   * Discovery bonus if target party has recent controversial actions.
   * Parties taking bold stances generate more research opportunities.
   */
  CONTROVERSIAL_ACTION_BONUS: 0.05,

  /**
   * AP threshold for rolling higher severity scandals.
   * Below this, mostly minor scandals are found.
   */
  MODERATE_SEVERITY_THRESHOLD: 3,

  /**
   * AP threshold for chance at major scandals.
   */
  MAJOR_SEVERITY_THRESHOLD: 4,

  /**
   * Probability weights for scandal severity at high intensity (5 AP).
   * [minor, moderate, major]
   */
  SEVERITY_WEIGHTS_HIGH: [0.3, 0.5, 0.2] as const,

  /**
   * Probability weights for scandal severity at medium intensity (3-4 AP).
   */
  SEVERITY_WEIGHTS_MEDIUM: [0.5, 0.4, 0.1] as const,

  /**
   * Probability weights for scandal severity at low intensity (2 AP).
   */
  SEVERITY_WEIGHTS_LOW: [0.7, 0.25, 0.05] as const,
} as const

// ============================================================================
// Scandal Effects by Severity
// ============================================================================

/**
 * Effects applied when a scandal is discovered, by severity level.
 * These are the immediate effects; the scandal continues to affect the target
 * until its duration expires or it's resolved.
 */
export const SCANDAL_EFFECTS: Record<ScandalSeverity, ScandalSeverityEffects> = {
  minor: {
    recognition: -2,
    loyalty: -3,
    momentum: -5,
    prestige: -2,
    duration: 2,
  },
  moderate: {
    recognition: -5,
    loyalty: -8,
    momentum: -12,
    prestige: -5,
    duration: 4,
  },
  major: {
    recognition: -10,
    loyalty: -15,
    momentum: -20,
    prestige: -10,
    duration: 8,
  },
} as const

// ============================================================================
// Scandal Type Configuration
// ============================================================================

/**
 * Configuration for each scandal type with flavor text templates.
 */
export const SCANDAL_TYPES: Record<ScandalType, { label: string; description: string }> = {
  corruption: {
    label: 'Corruption',
    description: 'Financial impropriety or misuse of resources',
  },
  hypocrisy: {
    label: 'Hypocrisy',
    description: 'Acting against stated principles or flip-flopping',
  },
  personal_misconduct: {
    label: 'Personal Misconduct',
    description: 'Inappropriate personal behavior',
  },
  policy_failure: {
    label: 'Policy Failure',
    description: 'A policy decision that backfired or caused harm',
  },
} as const

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the configuration for an attack subtype.
 */
export function getAttackSubtypeConfig(subtype: AttackSubtype): AttackSubtypeConfig {
  return ATTACK_SUBTYPES[subtype]
}

/**
 * Get scandal effects for a given severity.
 */
export function getScandalEffects(severity: ScandalSeverity): ScandalSeverityEffects {
  return SCANDAL_EFFECTS[severity]
}

/**
 * Calculate the base discovery chance for opposition research.
 * @param apSpent - Action points spent on research
 * @returns Base discovery chance (0-1)
 */
export function calculateBaseDiscoveryChance(apSpent: number): number {
  return OPPOSITION_RESEARCH.BASE_DISCOVERY_CHANCE +
    (apSpent * OPPOSITION_RESEARCH.DISCOVERY_CHANCE_PER_AP)
}

/**
 * Get severity probability weights based on AP spent.
 * @param apSpent - Action points spent on research
 * @returns Array of [minor, moderate, major] probabilities
 */
export function getSeverityWeights(apSpent: number): readonly [number, number, number] {
  if (apSpent >= 5) {
    return OPPOSITION_RESEARCH.SEVERITY_WEIGHTS_HIGH
  } else if (apSpent >= OPPOSITION_RESEARCH.MODERATE_SEVERITY_THRESHOLD) {
    return OPPOSITION_RESEARCH.SEVERITY_WEIGHTS_MEDIUM
  }
  return OPPOSITION_RESEARCH.SEVERITY_WEIGHTS_LOW
}
